import {
  Platform,
  Image,
  type ImageProps,
  type ImageSourcePropType,
} from 'react-native';
import React, { useState } from 'react';

let FastImage: any = Image;
if (Platform.OS !== 'web') {
  const RNFastImage = require('react-native-fast-image-original');
  FastImage = RNFastImage.default || RNFastImage;
}

type FastImageProps = any;
type Source = any;

interface FastImageWithFallbackProps extends Omit<FastImageProps, 'source'> {
  source: Source | Source[] | ImageSourcePropType | number;
  fallbackSource?: ImageSourcePropType | number;
}

const TRANSPARENT_1PX: ImageSourcePropType = require("../../../assets/images/fallback.webp")

function FastImageWithFallback({
  source,
  fallbackSource,
  onError,
  ...props
}: FastImageWithFallbackProps) {
  const [hasError, setHasError] = useState(false);

  // normaliza string, objeto e arrays
  const normalizeOne = (src: any): ImageSourcePropType =>
    typeof src === 'string' ? { uri: src } : src;

  const normalizeSource = (src: any): any => {
    if (Array.isArray(src)) return src.map(normalizeOne);
    return normalizeOne(src);
  };

  const sourceIsEmpty = (src: any): boolean => {
    if (!src) return true;
    if (typeof src === 'number') return false;
    if (Array.isArray(src)) {
      if (src.length === 0) return true;
      // vazio se todos no array não têm uri válida
      return src.every(item => {
        if (typeof item === 'number') return false;
        if (item && typeof item === 'object' && 'uri' in item) {
          const uri = (item as any).uri;
          return !uri || String(uri).trim().length === 0;
        }
        return true;
      });
    }
    if (typeof src === 'object' && 'uri' in src) {
      const uri = (src as any).uri;
      return !uri || String(uri).trim().length === 0;
    }
    return true;
  };

  const normalizedSource = normalizeSource(source);
  const isSourceEmpty = sourceIsEmpty(normalizedSource);
  const shouldUseFallback = hasError || isSourceEmpty;

  // Se deve usar fallback e temos fallbackSource => usa Image normal com fallback
  if (shouldUseFallback && fallbackSource) {
    return <Image {...(props as ImageProps)} source={normalizeSource(fallbackSource)} />;
  }

  // Sem fallbackSource:
  if (shouldUseFallback && !fallbackSource) {
    // Web mantém comportamento atual (não renderiza nada)
    if (Platform.OS === 'web') return null;

    // Nativo: mantém o espaço com um placeholder transparente 1x1
   return <Image {...(props as ImageProps)} source={TRANSPARENT_1PX} />;
  }

  const CommonImage = Platform.OS === 'web' ? Image : FastImage;

  return (
    <CommonImage
      {...props}
      source={normalizedSource}
      onError={(e: any) => {
        setHasError(true);
        onError?.(e);
      }}
    />
  );
}

export default FastImageWithFallback;
